import { useEffect, useState } from "react";
import { getSheetValues } from "../../service/googlesheetService";
import { Oval } from "react-loader-spinner";

import Cookies from "js-cookie";
import {
  IResult,
  IJob,
  IExpertise,
  IMajorDomain,
  IResultDisplay,
} from "../../Interfaces";
import {
  Radialbar,
  RadarComponent,
  ExpertiseList,
  JobBalance,
} from "./Features";
import { useHistory } from "react-router-dom";
import { IoIosArrowBack } from "react-icons/io";
import "./Result.css";
import useWindowDimensions from "../../Hooks/useWindowDimensions";
import { Score } from "../../Entities/Score";

const JOB_NAME_INDEX = 0;
const EXPERTISE_NAME_INDEX = 1;
const SCORE_INDEX = 4;
const MAJOR_DOMAIN_INDEX = 2;
const REDUCER_INITIALISATION = 0;
const MAX_SCORE_QUESTION = 4;

const Result = () => {
  const [userScore, setUserScore] = useState<{
    score: number | null;
    job: number | null;
  }>({ score: null, job: null });
  const [data, setData] = useState<IResultDisplay[]>([]);
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [selected, setSelected] = useState<IJob | null>(null);
  const [expertiseData, setExpertiseData] = useState<IResultDisplay | null>(
    null
  );
  const [indexExpertise, setIndexExpertise] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const { width } = useWindowDimensions();
  const isMobile = width <= 500;
  
  //Transform Data into result
  const transformDataIntoResult = (data: string[]) => {
    const result = new Score(data);

    return result.result;
  };
  
  const getFinishedFormData = async (user: any, token: string) => {
    const dataResponse = await getSheetValues(token, user.googleId, "A:K");
    
    const result = transformDataIntoResult(dataResponse);
    const data = display(result);

    return data;
  };

  //parse user data for display
  const processScoreItems = (data: IResult[]) => {
    const mappedScoreItems: IResultDisplay[] = data.map((item: IResult) => {
      return {
        ...item,
        scoreItems: item.scoreItems.map((scoreItem: any) => {
          return {
            name: scoreItem.name,
            pourcentage: Math.round(
              (scoreItem.userScore /
                (scoreItem.totalQuestionNumber * MAX_SCORE_QUESTION)) *
                100
            ),
            max: 100,
          };
        }),
      };
    });

    return mappedScoreItems;
  };

  //Score calculation
  const calculateScore = (array: string[][], index: number): number => {
    const score = array.reduce(
      (accumulator: number, currentValue: string[]) =>
        accumulator + parseInt(currentValue[index]),
      REDUCER_INITIALISATION
    );
    const scoreMax = array.length * MAX_SCORE_QUESTION;

    return Math.round((score / scoreMax) * 100);
  };

  //Filter by particular parameter
  const filterArrayByParameter = (
    array: string[][],
    stringToCompare: string,
    indexToFilter: number
  ): string[][] => {
    const newArray = array.filter((item: string[]) => {
      return item[indexToFilter] === stringToCompare;
    });
    return newArray;
  };

  const parseArrayForDetail = (
    arrayToMap: Set<string>,
    arrayToFilter: string[][]
  ): IMajorDomain[] => {
    return Array.from(arrayToMap).map((domain: string) => {
      //get major domain by job for score calculation
      const getMajorDomainPerJob: string[][] = filterArrayByParameter(
        arrayToFilter,
        domain,
        MAJOR_DOMAIN_INDEX
      );

      return {
        domain: domain,
        scores: calculateScore(getMajorDomainPerJob, SCORE_INDEX),
      };
    });
  };

  //get unique name from an array of string
  const getUniqueNameInSetType = (
    data: string[],
    index: number
  ): Set<string> => {
    const arrayOfName: string[] = data.map((item: string) => {
      const name = item[index];
      return name;
    });

    const uniqueName = new Set(arrayOfName);

    return uniqueName;
  };

  const parseDomainDetailForJobsData = (
    setOfUniqueName: Set<string>,
    arrayToFilter: string[][]
  ): IExpertise[] => {
    return Array.from(setOfUniqueName).map((expertise: string) => {
      //get expertise domain from job array
      const getExpertisesPerJob: string[][] = filterArrayByParameter(
        arrayToFilter,
        expertise,
        EXPERTISE_NAME_INDEX
      );

      // get unique major domain
      const majorDomainArray: string[] = getExpertisesPerJob.map(
        (item: string[]) => item[MAJOR_DOMAIN_INDEX]
      );

      const uniqueMajorDomain = new Set(majorDomainArray);

      const getMajorDomain = parseArrayForDetail(
        uniqueMajorDomain,
        arrayToFilter
      );

      return {
        expertiseName: expertise,
        scoreByExpertise: calculateScore(getExpertisesPerJob, SCORE_INDEX),
        scoreItems: getMajorDomain,
      };
    });
  };

  //initialisation job score
  const parseJobsData = (
    arrayGlobalJob: any,
    jobArray: Set<string>,
    expertiseArray: Set<string>
  ) => {
    //get an array by job
    const parseJobsIntoObject: IJob[] = Array.from(jobArray).map(
      (job: string) => {
        //filter array by job
        const splitJobintoArray: string[][] = filterArrayByParameter(
          arrayGlobalJob,
          job,
          JOB_NAME_INDEX
        );

        const domainDetail = parseDomainDetailForJobsData(
          expertiseArray,
          splitJobintoArray
        );
        return {
          jobName: job,
          scoreByJob: calculateScore(splitJobintoArray, SCORE_INDEX),
          domain: domainDetail,
        };
      }
    );
    setJobs(parseJobsIntoObject);
  };

  //get all jobs to compare with user's skills
  const getJobValue = async (token: string) => {
    try{
      const jobsDataSheet = await getSheetValues(token, "MetierBPM", "A2:F");

      const uniqueJobName = getUniqueNameInSetType(jobsDataSheet, JOB_NAME_INDEX);
      const uniqueExpertiseDomain = getUniqueNameInSetType(
        jobsDataSheet,
        EXPERTISE_NAME_INDEX
      );
  
      return parseJobsData(jobsDataSheet, uniqueJobName, uniqueExpertiseDomain);
    }catch(error){
      console.log(error);     
    }

  };

  
  const display = (resultData: IResult[]) => {
    //user global score
    const scoreGlobal = resultData.reduce(
      (accumulator: number, currentValue: IResult) =>
        accumulator + currentValue.globalScore,
      REDUCER_INITIALISATION
    );

    //get max score
    const maxScore = resultData.reduce(
      (accumulator: number, currentValue: IResult) =>
        accumulator + currentValue.maxScore,
      REDUCER_INITIALISATION
    );
    const newResultData = processScoreItems(resultData);
      
    setData(newResultData);

    setExpertiseData(newResultData[indexExpertise]);
    setUserScore({
      score: Math.round((scoreGlobal / maxScore) * 100),
      job: selected ? selected.scoreByJob : 0,
    });    

    return resultData;
  };

  useEffect(() => {
    setIsLoading(true)
    const userCookie = Cookies.get("user");
    const token = localStorage.getItem("idToken");
    if (userCookie && token) {
      const userParse = JSON.parse(userCookie);

      const getResultUser = async () => {
        const dataUserParse = await getFinishedFormData(userParse, token);
        return dataUserParse;
      };
      getResultUser();
      getJobValue(token);
    }
    setIsLoading(false)
    // eslint-disable-next-line
  }, [selected]);

  return (
    <div className="result">
      <div className="resultHeader">
        <div
          className="backToExpertise"
          onClick={() => {
            history.goBack();
          }}
        >
          {isMobile ? (
            <IoIosArrowBack />
          ) : (
            <p>Retour à la liste des compétences</p>
          )}
        </div>
        <div className="titleResult">Tes scores</div>
      </div>
      {isLoading ? (
        <div className="bodyResultBackground-test">
          <Oval
            ariaLabel="loading-indicator"
            wrapperStyle={{ display: "flex" }}
            height={100}
            width={100}
            strokeWidth={5}
            strokeWidthSecondary={3}
            secondaryColor="#FCA2AE"
            color="#FFFFFF"
          />
        </div>
      ) : (
        <div className="bodyResultBackground">
          <div className="bodyResult">
            <ExpertiseList
              nameJob={selected?.jobName}
              resultData={data}
              setExpertiseData={setExpertiseData}
              setIndexExpertise = {setIndexExpertise}
            />
            <div className="resultUser">
              <div className="userBalance">
                <h2 className="titlePartUser">Attendu par métier</h2>
                <h3>
                  Compare ton niveau d’expertise avec un des métiers de la tribu
                </h3>
                <JobBalance
                  loader={isLoading}
                  jobs={jobs}
                  isMobile={isMobile}
                  setSelected={setSelected}
                />
              </div>
              <div className="userResult">
                <h2 className="titlePartUser">Résultats</h2>
                <div className="userResultGraph">
                  {<Radialbar loader={isLoading} score={userScore} />}
                  {expertiseData && (
                    <RadarComponent
                      jobDataBalance={selected}
                      expertiseData={expertiseData}
                      isMobile={isMobile}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Result;
